import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2, ImageIcon } from 'lucide-react';

import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { SaveBar } from '@/components/ui/save-bar';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { MediaPicker } from '@/components/media/picker';
import { useBrief, useUpdateBriefSlice, useSaveAndRebuildBrief } from '@/lib/brief';
import { useMediaHost } from '@/lib/media';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/brief/team')({
  staticData: { crumb: 'Zespół' },
  component: TeamPage,
});

const MemberSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]{2,40}$/u, 'Identyfikator: małe litery, cyfry i myślniki (2-40)'),
  full_name: z.string().min(3, 'Min. 3 znaki').max(120),
  title: z.string().max(80).optional().or(z.literal('')),
  kiwl: z.string().regex(/^[A-Z]{1,3}-\d{1,6}$/u, 'KIWL np. WR-1234').optional().or(z.literal('')),
  pwz: z.string().regex(/^\d{7}$/u, 'PWZ to 7 cyfr').optional().or(z.literal('')),
  specialties: z.string().max(1000).optional().or(z.literal('')),
  bio_facts: z.string().max(4000).optional().or(z.literal('')),
});
type MemberInput = z.infer<typeof MemberSchema>;

interface StoredZnanyLekarz {
  doctor_slug: string;
  profile_url: string;
  widget_type?: 'big_with_calendar' | 'small' | 'button' | 'badge';
  specialization_ids?: number[];
  hide_branding?: boolean;
}

interface StoredMember {
  id: string;
  full_name: string;
  title?: string;
  kiwl?: string;
  pwz?: string;
  specialties?: string[];
  species_focus?: string[];
  bio_facts?: string[];
  photo_url?: string;
  photo_asset_id?: string;
  znanylekarz?: StoredZnanyLekarz;
}

function TeamPage() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const saveAndRebuild = useSaveAndRebuildBrief();
  const mediaHost = useMediaHost();
  const [members, setMembers] = React.useState<StoredMember[]>(
    () => (brief?.team ?? []) as StoredMember[]
  );
  const [editingIdx, setEditingIdx] = React.useState<number | null>(null);
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');
  const [dirty, setDirty] = React.useState(false);

  // Re-sync when brief reloads (e.g. after first fetch).
  React.useEffect(() => {
    if (brief?.team && !dirty) setMembers(brief.team as StoredMember[]);
  }, [brief?.team, dirty]);

  const slugify = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);

  const onSave = async () => {
    setSavedStatus('idle');
    try {
      await update.mutateAsync({ slice: 'team', data: members });
      setSavedStatus('saved');
      setDirty(false);
    } catch {
      setSavedStatus('error');
    }
  };

  const onSaveAndRebuild = async () => {
    setSavedStatus('idle');
    try {
      await saveAndRebuild.mutateAsync({ slice: 'team', data: members });
      setSavedStatus('saved');
      setDirty(false);
    } catch {
      setSavedStatus('error');
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
      className="space-y-6"
    >
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">Zespół</h1>
        <p className="mt-1 text-sm text-text-muted">
          Lekarze i personel kliniki. Numer PWZ / KIWL pokazujemy w stopce karty zespołu.
          Bez numeru, członek zespołu nie pojawi się na publicznej stronie.
        </p>
      </header>

      <Button
        type="button"
        variant="secondary"
        onClick={() => {
          setMembers((prev) => [
            ...prev,
            { id: `osoba-${prev.length + 1}`, full_name: 'Nowy członek zespołu' },
          ]);
          setEditingIdx(members.length);
          setDirty(true);
        }}
      >
        <Plus className="size-4" aria-hidden="true" /> Dodaj członka zespołu
      </Button>

      <ul role="list" className="grid gap-3 md:grid-cols-2">
        {members.map((m, idx) => (
          <li
            key={m.id}
            className="flex items-start gap-3 rounded-[var(--radius-card)] bg-bg p-4 ring-1 ring-black/5"
          >
            {m.photo_url ? (
              <img
                src={`${mediaHost}${m.photo_url}`}
                alt=""
                loading="lazy"
                className="size-14 shrink-0 rounded-full object-cover ring-1 ring-black/5"
              />
            ) : (
              <div className="grid size-14 shrink-0 place-items-center rounded-full bg-surface text-xs text-text-subtle ring-1 ring-black/5">
                {m.full_name?.split(' ').map((s) => s[0]).join('').slice(0, 2) || '?'}
              </div>
            )}
            <button
              type="button"
              className="flex-1 text-left"
              onClick={() => setEditingIdx(idx)}
            >
              <p className="font-medium text-text">{m.full_name || '(brak nazwiska)'}</p>
              <p className="mt-0.5 text-xs text-text-muted">
                {m.title || 'Brak tytułu'} ·{' '}
                {m.kiwl || m.pwz ? (
                  <span className="font-mono text-text-subtle">{m.kiwl || m.pwz}</span>
                ) : (
                  <span className="text-warning">Brak numeru PWZ / KIWL</span>
                )}
              </p>
              {(m.specialties ?? []).length > 0 ? (
                <ul role="list" className="mt-3 flex flex-wrap gap-1.5">
                  {(m.specialties ?? []).slice(0, 4).map((s, i) => (
                    <li
                      key={i}
                      className="rounded-full bg-surface px-2 py-0.5 text-xs text-text-muted ring-1 ring-black/5"
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              ) : null}
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  type="button"
                  className="p-2 text-text-subtle hover:text-error"
                  aria-label="Usuń członka zespołu"
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Usunąć {m.full_name || 'członka zespołu'}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Zniknie z publicznej strony przy następnej publikacji. Operacja jest
                    lokalna — pozostałe zmiany zatwierdzasz przyciskiem „Zapisz".
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Anuluj</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      setMembers((prev) => prev.filter((_, i) => i !== idx));
                      setDirty(true);
                      if (editingIdx === idx) setEditingIdx(null);
                    }}
                  >
                    Usuń
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </li>
        ))}
      </ul>

      {members.length === 0 ? (
        <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
          Brak członków zespołu. Dodaj przynajmniej jednego lekarza, aby uruchomić sekcję
          „Zespół" na stronie publicznej.
        </p>
      ) : null}

      {editingIdx !== null ? (
        <MemberEditor
          key={members[editingIdx]?.id}
          member={members[editingIdx]!}
          slugify={slugify}
          onClose={() => setEditingIdx(null)}
          onSave={(next) => {
            setMembers((prev) => prev.map((m, i) => (i === editingIdx ? next : m)));
            setEditingIdx(null);
            setDirty(true);
          }}
        />
      ) : null}

      <SaveBar
        saving={update.isPending}
        dirty={dirty}
        status={savedStatus}
        rebuilding={saveAndRebuild.isPending}
        onSaveAndRebuild={onSaveAndRebuild}
        onCancel={() => {
          setMembers((brief?.team ?? []) as StoredMember[]);
          setDirty(false);
        }}
      />
    </form>
  );
}

function MemberEditor({
  member,
  slugify,
  onClose,
  onSave,
}: {
  member: StoredMember;
  slugify: (s: string) => string;
  onClose: () => void;
  onSave: (next: StoredMember) => void;
}) {
  const form = useForm<MemberInput>({
    resolver: zodResolver(MemberSchema),
    defaultValues: {
      id: member.id,
      full_name: member.full_name,
      title: member.title ?? '',
      kiwl: member.kiwl ?? '',
      pwz: member.pwz ?? '',
      specialties: (member.specialties ?? []).join(', '),
      bio_facts: (member.bio_facts ?? []).join('\n'),
    },
  });

  // Auto-slug on first creation if full_name changes & id matches placeholder.
  const watchedName = form.watch('full_name');
  React.useEffect(() => {
    if (/^osoba-\d+$/.test(form.getValues('id'))) {
      const slug = slugify(watchedName);
      if (slug) form.setValue('id', slug);
    }
  }, [watchedName, form, slugify]);

  const [photoUrl, setPhotoUrl] = React.useState<string | undefined>(member.photo_url);
  const [photoAssetId, setPhotoAssetId] = React.useState<string | undefined>(member.photo_asset_id);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const mediaHost = useMediaHost();
  const [znanylekarz, setZnanylekarz] = React.useState<StoredZnanyLekarz | undefined>(
    member.znanylekarz
  );

  const submit = (data: MemberInput) => {
    const next: StoredMember = {
      id: data.id,
      full_name: data.full_name,
      title: data.title || undefined,
      kiwl: data.kiwl || undefined,
      pwz: data.pwz || undefined,
      specialties: data.specialties
        ? data.specialties
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      species_focus: member.species_focus ?? [],
      bio_facts: data.bio_facts
        ? data.bio_facts
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      photo_url: photoUrl,
      photo_asset_id: photoAssetId,
      znanylekarz,
    };
    onSave(next);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edytuj członka zespołu</DialogTitle>
          <DialogDescription>
            Dane lekarza + numer PWZ/KIWL + zdjęcie + powiązanie ze znanylekarz.pl.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={form.handleSubmit(submit)}
          className="space-y-4"
          noValidate
        >
          <div className="flex items-start gap-4 rounded-md bg-surface p-4 ring-1 ring-black/5">
            {photoUrl ? (
              <img
                src={`${mediaHost}${photoUrl}`}
                alt=""
                className="size-20 rounded-full object-cover ring-1 ring-black/5"
              />
            ) : (
              <div className="grid size-20 place-items-center rounded-full bg-bg text-text-subtle ring-1 ring-black/5">
                <ImageIcon className="size-7" aria-hidden="true" />
              </div>
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-text">Zdjęcie profilowe</p>
              <p className="mt-0.5 text-xs text-text-muted">
                Wybierz z biblioteki mediów (zaleca się typ „Zdjęcie zespołu" z podpisaną
                zgodą).
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="secondary" onClick={() => setPickerOpen(true)}>
                  {photoUrl ? 'Zmień zdjęcie' : 'Wybierz zdjęcie z biblioteki'}
                </Button>
                {photoUrl ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setPhotoUrl(undefined);
                      setPhotoAssetId(undefined);
                    }}
                  >
                    Usuń powiązanie
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          {pickerOpen ? (
            <MediaPicker
              title="Wybierz zdjęcie członka zespołu"
              kinds={['team_photo', 'other']}
              selectedId={photoAssetId ?? null}
              onSelect={(asset) => {
                setPhotoUrl(asset.public_url);
                setPhotoAssetId(asset.id);
                setPickerOpen(false);
              }}
              onClear={() => {
                setPhotoUrl(undefined);
                setPhotoAssetId(undefined);
                setPickerOpen(false);
              }}
              onClose={() => setPickerOpen(false)}
            />
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="Pełne imię i nazwisko"
              htmlFor="full_name"
              required
              error={form.formState.errors.full_name?.message}
            >
              <Input id="full_name" autoComplete="name" {...form.register('full_name')} />
            </Field>
            <Field
              label="Tytuł zawodowy"
              htmlFor="title"
              hint="lek. wet., dr n. med…"
              error={form.formState.errors.title?.message}
            >
              <Input id="title" {...form.register('title')} />
            </Field>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label="KIWL"
              htmlFor="kiwl"
              hint="dla weterynarzy"
              error={form.formState.errors.kiwl?.message}
            >
              <Input id="kiwl" placeholder="WR-1234" {...form.register('kiwl')} />
            </Field>
            <Field
              label="PWZ"
              htmlFor="pwz"
              hint="dla lekarzy"
              error={form.formState.errors.pwz?.message}
            >
              <Input id="pwz" inputMode="numeric" placeholder="1234567" {...form.register('pwz')} />
            </Field>
          </div>

          <Field
            label="Specjalizacje"
            htmlFor="specialties"
            hint="oddziel przecinkami"
            error={form.formState.errors.specialties?.message}
          >
            <Input
              id="specialties"
              placeholder="stomatologia, chirurgia"
              {...form.register('specialties')}
            />
          </Field>

          <Field
            label="Fakty biograficzne"
            htmlFor="bio_facts"
            hint="jeden fakt w linii, max 20 linii"
            error={form.formState.errors.bio_facts?.message}
          >
            <Textarea
              id="bio_facts"
              rows={6}
              placeholder={`Absolwentka SGGW Warszawa\n10 lat doświadczenia\nCzłonek European Veterinary Society`}
              {...form.register('bio_facts')}
            />
          </Field>

          <Field
            label="Identyfikator"
            htmlFor="id"
            hint="krótki, w URL-u (np. anna-kowalska)"
            error={form.formState.errors.id?.message}
          >
            <Input id="id" className="font-mono text-xs" {...form.register('id')} />
          </Field>

          <ZnanyLekarzCard binding={znanylekarz} onChange={setZnanylekarz} />

          <div className="mt-2 flex justify-end gap-3 border-t border-border pt-4">
            <Button type="button" variant="ghost" onClick={onClose}>
              Anuluj
            </Button>
            <Button type="submit">Zapisz zmiany lokalnie</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * znanylekarz.pl profile URL parser. Validates:
 *   - host is www.znanylekarz.pl
 *   - extracts doctor_slug from the first path segment
 *   - requires #address-id fragment
 * Returns null when invalid; the wizard shows the reason.
 */
function parseZnanyLekarzUrl(input: string):
  | { doctor_slug: string; address_id: number; normalized_url: string }
  | { error: string } {
  let u: URL;
  try {
    u = new URL(input.trim());
  } catch {
    return { error: 'Nieprawidłowy URL.' };
  }
  if (u.host !== 'www.znanylekarz.pl' && u.host !== 'znanylekarz.pl') {
    return { error: 'URL musi pochodzić z www.znanylekarz.pl.' };
  }
  const seg = u.pathname.replace(/^\//, '').split('/')[0] ?? '';
  if (!/^[a-z0-9-]{2,80}$/.test(seg)) {
    return { error: 'Nie udało się wyodrębnić doctor_slug z URL.' };
  }
  // Fragment is required per acceptance criteria — accept #address-id=N
  // or #address-id-N (the latter is what znanylekarz currently emits).
  const frag = u.hash.replace(/^#/, '');
  const fragMatch =
    /^address-id[=-](\d+)/i.exec(frag) ?? /address[_-]?id[=:]?(\d+)/i.exec(frag);
  if (!fragMatch) {
    return { error: 'URL musi zawierać fragment #address-id (skopiuj z strony znanylekarz).' };
  }
  return {
    doctor_slug: seg,
    address_id: Number(fragMatch[1]),
    normalized_url: u.toString(),
  };
}

function ZnanyLekarzCard({
  binding,
  onChange,
}: {
  binding: StoredZnanyLekarz | undefined;
  onChange: (next: StoredZnanyLekarz | undefined) => void;
}) {
  const [open, setOpen] = React.useState(false);
  if (!binding) {
    return (
      <div className="rounded-md bg-surface p-4 ring-1 ring-black/5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-text">Rezerwacja online (znanylekarz.pl)</p>
            <p className="mt-0.5 text-xs text-text-muted">
              Powiąż profil tego lekarza ze znanylekarz.pl — widżet rezerwacyjny pojawi się
              na stronie kliniki przy nazwisku.
            </p>
          </div>
          <Button type="button" size="sm" variant="secondary" onClick={() => setOpen(true)}>
            Skonfiguruj
          </Button>
        </div>
        {open ? (
          <ZnanyLekarzWizard
            initial={null}
            onClose={() => setOpen(false)}
            onSave={(next) => {
              onChange(next);
              setOpen(false);
            }}
          />
        ) : null}
      </div>
    );
  }
  return (
    <div className="rounded-md bg-success/5 p-4 ring-1 ring-success/20">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-text">
            Rezerwacja online aktywna
          </p>
          <p className="mt-1 break-all text-xs text-text-muted">
            <span className="font-mono">{binding.doctor_slug}</span>
            {' — '}
            <a
              href={binding.profile_url}
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:underline"
            >
              {binding.profile_url}
            </a>
          </p>
          <p className="mt-0.5 text-xs text-text-subtle">
            Widżet: <span className="font-mono">{binding.widget_type ?? 'big_with_calendar'}</span>
            {binding.hide_branding === false ? ' · z logo znanylekarz' : ' · bez logo'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(true)}>
            Edytuj
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button type="button" size="sm" variant="ghost">
                Usuń powiązanie
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Usunąć powiązanie ze znanylekarz?</AlertDialogTitle>
                <AlertDialogDescription>
                  Widżet rezerwacji zniknie z karty tego lekarza po następnej publikacji.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Anuluj</AlertDialogCancel>
                <AlertDialogAction onClick={() => onChange(undefined)}>Usuń</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      {open ? (
        <ZnanyLekarzWizard
          initial={binding}
          onClose={() => setOpen(false)}
          onSave={(next) => {
            onChange(next);
            setOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function ZnanyLekarzWizard({
  initial,
  onSave,
  onClose,
}: {
  initial: StoredZnanyLekarz | null;
  onSave: (next: StoredZnanyLekarz) => void;
  onClose: () => void;
}) {
  const [url, setUrl] = React.useState(initial?.profile_url ?? '');
  const [widgetType, setWidgetType] = React.useState<
    'big_with_calendar' | 'small' | 'button' | 'badge'
  >(initial?.widget_type ?? 'big_with_calendar');
  const [hideBranding, setHideBranding] = React.useState(initial?.hide_branding ?? true);
  const parsed = url ? parseZnanyLekarzUrl(url) : null;
  const ok = parsed && 'doctor_slug' in parsed;

  const onCommit = () => {
    if (!ok) return;
    onSave({
      doctor_slug: parsed.doctor_slug,
      profile_url: parsed.normalized_url,
      widget_type: widgetType,
      hide_branding: hideBranding,
      specialization_ids: initial?.specialization_ids ?? [],
    });
  };

  return (
    <div className="mt-4 space-y-3 rounded-md bg-bg p-4 ring-1 ring-accent/30">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">
        Krok 1 — wklej URL profilu
      </p>
      <Field
        label="URL profilu znanylekarz.pl"
        htmlFor="zl-url"
        hint="Skopiuj z paska adresu pełny link, łącznie z fragmentem #address-id-…"
        error={parsed && 'error' in parsed ? parsed.error : undefined}
      >
        <Input
          id="zl-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.znanylekarz.pl/anna-kowalska/weterynarz#address-id-12345"
        />
      </Field>

      {ok ? (
        <>
          <div className="rounded-md bg-surface p-3 text-xs text-text-muted ring-1 ring-black/5">
            Wykryto lekarza: <span className="font-mono text-text">{parsed.doctor_slug}</span>{' '}
            · address-id: <span className="font-mono text-text">{parsed.address_id}</span>
          </div>

          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-text-subtle">
            Krok 2 — wariant widżetu
          </p>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {(['big_with_calendar', 'small', 'button', 'badge'] as const).map((wt) => (
              <button
                type="button"
                key={wt}
                onClick={() => setWidgetType(wt)}
                className={cn(
                  'rounded-md p-2 text-xs ring-1 transition',
                  widgetType === wt
                    ? 'bg-accent/10 text-accent ring-accent/30'
                    : 'bg-bg text-text-muted ring-border hover:ring-black/10'
                )}
              >
                {wt}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-text">
            <Switch checked={hideBranding} onCheckedChange={setHideBranding} />
            Ukryj branding znanylekarz na widżecie
          </label>

          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-text-subtle">
            Krok 3 — podgląd
          </p>
          <iframe
            title="Podgląd widżetu znanylekarz"
            src={parsed.normalized_url}
            className="h-72 w-full rounded-md ring-1 ring-black/5"
            referrerPolicy="no-referrer"
            sandbox="allow-scripts allow-same-origin"
          />
          <p className="text-[10px] text-text-subtle">
            Podgląd to pełna strona profilu — rzeczywisty widżet na Twojej stronie zostanie
            wbudowany przy nazwisku tego lekarza po publikacji.
          </p>
        </>
      ) : null}

      <div className="flex justify-end gap-2 border-t border-border pt-3">
        <Button type="button" variant="ghost" onClick={onClose}>
          Anuluj
        </Button>
        <Button type="button" onClick={onCommit} disabled={!ok}>
          {initial ? 'Zapisz' : 'Dodaj powiązanie'}
        </Button>
      </div>
    </div>
  );
}
